import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import swal from "sweetalert";
import {
  CheckPermission,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles, compressImageFile } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";

const CustomInput = ({ autoFocus, label, name, value, onChange, error }) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Input
      autoFocus={autoFocus}
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

// ---------------- Main Component (clone without dimensions and serial number) ----------------
const OtherIncomeTarbiatiCourseReimbursment = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");

  // ---------- State ----------
  const [loading, setLoading] = useState(false);
  const [expenseReceiptFile, setExpenseReceiptFile] = useState(null);
  const [incomeSummaryFile, setIncomeSummaryFile] = useState(null);

  const [formData, setFormData] = useState({
    selectedFinanceCategory: null,
    selectedAccount: null,
    rafiqBankId: null,
    date: new Date(),
    amount: "",
    memo: "",
  });

  const [errors, setErrors] = useState({});

  // ---------- Handlers ----------
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleExpenseReceiptChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1500 * 1024; // 1.5 MB
    let processedFile = file;

    if (file.type && file.type.startsWith("image/") && file.size > MAX_SIZE) {
      try {
        processedFile = await compressImageFile(file, 1400 * 1024);
      } catch (err) {
        console.error("Image compression error:", err);
      }
    }

    if (processedFile.size > MAX_SIZE) {
      setExpenseReceiptFile(null);
      setErrors((prev) => ({
        ...prev,
        expenseReceiptFile: <GetLanguageString props="file_size_limit_1_5mb" />,
      }));
      e.target.value = "";
    } else {
      setExpenseReceiptFile(processedFile);
      setErrors((prev) => ({ ...prev, expenseReceiptFile: "" }));
    }
  };

  const handleIncomeSummaryChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1500 * 1024; // 1.5 MB
    let processedFile = file;

    if (file.type && file.type.startsWith("image/") && file.size > MAX_SIZE) {
      try {
        processedFile = await compressImageFile(file, 1400 * 1024);
      } catch (err) {
        console.error("Image compression error:", err);
      }
    }

    if (processedFile.size > MAX_SIZE) {
      setIncomeSummaryFile(null);
      setErrors((prev) => ({
        ...prev,
        incomeSummaryFile: <GetLanguageString props="file_size_limit_1_5mb" />,
      }));
      e.target.value = "";
    } else {
      setIncomeSummaryFile(processedFile);
      setErrors((prev) => ({ ...prev, incomeSummaryFile: "" }));
    }
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const isValid = validate();
    if (!isValid) {
      return;
    }
    const payload = new FormData();
    payload.append("amount", formData.amount);
    payload.append("memo", formData.memo);
    payload.append(
      "date",
      formData.date ? formData.date.toISOString() : new Date().toISOString(),
    );
    payload.append("unitId", loggedInUnitId);

    if (formData.selectedFinanceCategory) {
      if (formData.selectedFinanceCategory.label1) {
        payload.append("category.label1", formData.selectedFinanceCategory.label1);
      }
      if (formData.selectedFinanceCategory.value) {
        payload.append("category.value", formData.selectedFinanceCategory.value);
      }
    }

    if (formData.selectedAccount) {
      if (formData.selectedAccount.value) {
        payload.append("financeAccount.value", formData.selectedAccount.value);
      }
      if (formData.selectedAccount.label) {
        payload.append("financeAccount.label", formData.selectedAccount.label);
      }
    }

    if (expenseReceiptFile) {
      payload.append("expenseReceiptFile", expenseReceiptFile);
    }
    if (incomeSummaryFile) {
      payload.append("incomeSummaryFile", incomeSummaryFile);
    }

    setLoading(true);
    ApiFinanceLocal()
      .AddOtherIncomeTarbiatiCourseReimbursment(payload)
      .then((res) => {
        const data = res.data;

        if (data?.statusCode === "200") {
          swal({
            title: "Success",
            text:
              t(data.message) ||
              t(
                "finance_other_income_reimbursment_for_tarbiati_course_requested",
              ),
            icon: "success",
            buttons: "OK",
          }).then(() => {
            history.goBack();
          });
          return;
        }

        swal({
          title: "Error",
          text: data?.message || "Failed to add Other Income",
          icon: "error",
          buttons: "OK",
        });

        setLoading(false);
      })
      .catch(() => {
        swal({
          title: "Error",
          text: "Unable to connect to server. Please try again.",
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
      });
  };

  // ---------- Effects ----------
  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetFinanceSubCategoriesListByType("income")
      .then((res) => {
        // Prefer a category that references markaz; fallback to first
        const preferred = res.data.find(
          (c) =>
            c.label1 &&
            c.label1.includes("reimbursment") &&
            c.label1.includes("tarbiati"),
        );
        setFormData((prev) => ({
          ...prev,
          selectedFinanceCategory: preferred || res.data[0] || null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading(false));
  }, [unitTypeName]);

  // ---------- Effects ----------
  useEffect(() => {
    if (!formData.selectedFinanceCategory) return;
    setLoading(true);
    setFormData((prev) => ({
      ...prev,
      selectedAccount: null,
      selectedDimension: null,
    }));

    // Accounts
    ApiFinance()
      .GetFinanceSubCategoryAccountsById(formData.selectedFinanceCategory.value)
      .then((res) => {
        setFormData((prev) => ({
          ...prev,
          selectedAccount: res.data[0] || null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading(false));
  }, [formData.selectedFinanceCategory]);
  // ---------- Validation ----------
  const validate = () => {
    const temp = {};
    temp.amount = formData.amount ? (
      ""
    ) : (
      <GetLanguageString props="funds_add_fund_amount_paid_error" />
    );

    temp.memo = formData.memo && formData.memo.trim() !== "" ? (
      ""
    ) : (
      <GetLanguageString props="common_field_required_error" />
    );

    const dateError = validateTransactionDate(formData.date);
    if (dateError) {
      temp.date = t(dateError);
    }

    const maxSize = 1500 * 1024;
    if (!expenseReceiptFile) {
      temp.expenseReceiptFile = (
        <GetLanguageString props="expense_receipts_required" />
      );
    } else if (expenseReceiptFile.size > maxSize) {
      temp.expenseReceiptFile = (
        <GetLanguageString props="file_size_limit_1_5mb" />
      );
    } else {
      temp.expenseReceiptFile = "";
    }

    if (!incomeSummaryFile) {
      temp.incomeSummaryFile = (
        <GetLanguageString props="income_summary_required" />
      );
    } else if (incomeSummaryFile.size > maxSize) {
      temp.incomeSummaryFile = (
        <GetLanguageString props="file_size_limit_1_5mb" />
      );
    } else {
      temp.incomeSummaryFile = "";
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  // ---------- Render ----------
  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>
            {
              <GetLanguageString props="funds_other_income_tarbiati_course_reimbursment" />
            }
          </h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                {/* Income head is auto-selected from API; no select shown */}

                <Col md="4">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      minDate={getMinMaxDates(formData.date).minDate}
                      maxDate={getMinMaxDates(formData.date).maxDate}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.date && (
                      <h4 className="text-error">{errors.date}</h4>
                    )}
                  </FormGroup>
                </Col>

                <Col md="4">
                  <CustomInput
                    autoFocus
                    name="amount"
                    label="funds_add_fund_amount"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    error={errors.amount}
                  />
                </Col>

                <Col md="4">
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>
              </Row>

              {/* Dual Mandatory Attachments */}
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="expense_receipts_attachment" />}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="file"
                      accept="image/*,application/pdf"
                      name="expenseReceiptFile"
                      className="form-control"
                      onChange={handleExpenseReceiptChange}
                      invalid={!!errors.expenseReceiptFile}
                    />
                    {errors.expenseReceiptFile && (
                      <h4 className="text-error mt-1">{errors.expenseReceiptFile}</h4>
                    )}
                    {expenseReceiptFile && (
                      <div
                        className="d-flex align-items-center justify-content-between p-2 mt-2 rounded"
                        style={{
                          backgroundColor: "#ECFDF5",
                          border: "1px solid #A7F3D0",
                          fontSize: "12px",
                          color: "#065F46",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "80%" }}>
                          <i className="fa fa-file text-success"></i>
                          <span className="text-truncate fw-bold">{expenseReceiptFile.name}</span>
                          <span className="badge bg-light text-muted border px-1">
                            {(expenseReceiptFile.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="text-danger p-0 ms-2 text-decoration-none"
                          onClick={() => setExpenseReceiptFile(null)}
                          title={t("remove_file") || "Remove file"}
                          type="button"
                        >
                          <i className="fa fa-times"></i>
                        </Button>
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="income_summary_attachment" />}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="file"
                      accept="image/*,application/pdf"
                      name="incomeSummaryFile"
                      className="form-control"
                      onChange={handleIncomeSummaryChange}
                      invalid={!!errors.incomeSummaryFile}
                    />
                    {errors.incomeSummaryFile && (
                      <h4 className="text-error mt-1">{errors.incomeSummaryFile}</h4>
                    )}
                    {incomeSummaryFile && (
                      <div
                        className="d-flex align-items-center justify-content-between p-2 mt-2 rounded"
                        style={{
                          backgroundColor: "#ECFDF5",
                          border: "1px solid #A7F3D0",
                          fontSize: "12px",
                          color: "#065F46",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "80%" }}>
                          <i className="fa fa-file text-success"></i>
                          <span className="text-truncate fw-bold">{incomeSummaryFile.name}</span>
                          <span className="badge bg-light text-muted border px-1">
                            {(incomeSummaryFile.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="text-danger p-0 ms-2 text-decoration-none"
                          onClick={() => setIncomeSummaryFile(null)}
                          title={t("remove_file") || "Remove file"}
                          type="button"
                        >
                          <i className="fa fa-times"></i>
                        </Button>
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  {CheckPermission("CanAddFunds") ? (
                    <>
                      <Button color="white" onClick={() => history.goBack()}>
                        {<GetLanguageString props="common_cancel" />}
                      </Button>
                      &nbsp;&nbsp;
                      <Button
                        disabled={loading}
                        color="primary"
                        onClick={() => handleSubmit(false)}
                      >
                        {<GetLanguageString props="funds_add_income" />}
                      </Button>
                    </>
                  ) : null}
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(OtherIncomeTarbiatiCourseReimbursment);
